import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const LOGO_URL = "/assets/ENPOWER-on-dark.svg";

export const Footer = () => {
  return (
    <footer className="bg-[#122A5C] text-white" data-testid="main-footer">
      {/* CTA Section */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 lg:flex-row lg:gap-8">
            <div className="text-center lg:text-left">
              <h3 className="font-heading mb-3 text-2xl font-bold md:text-4xl">
                Bereit für die Energiewende?
              </h3>
              <p className="text-base text-white/70 md:text-lg">
                Lassen Sie uns gemeinsam Ihr Projekt realisieren.
              </p>
            </div>
            <Link to="/angebot-anfordern">
              <Button 
                data-testid="footer-cta-button"
                className="flex items-center gap-2 rounded-full bg-[#E07000] px-7 py-5 text-base font-semibold text-white shadow-xl shadow-orange-600/30 hover:bg-[#C56200] md:px-10 md:text-lg btn-animate"
              >
                Jetzt Angebot anfordern
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 md:py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-4">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="EN:POWER Logo ENPOWER"
                width={1120}
                height={160}
                loading="lazy"
                decoding="async"
                className="h-auto w-44 md:w-56 lg:w-64"
              />
            </div>
            <p className="text-white/70 mb-6 leading-relaxed">
              Ihr kompetenter Partner für nachhaltige Energieprojekte. Wir entwickeln wirtschaftlich attraktive Lösungen für Kleinspeicher, Batteriespeicher, Freiflächen-Photovoltaik und Dachverpachtung.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/leistungen" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Leistungen
                </Link>
              </li>
              <li>
                <Link to="/pachtrechner" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Pachtrechner
                </Link>
              </li>
              <li>
                <Link to="/ueber-uns" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Über uns
                </Link>
              </li>
              <li>
                <Link to="/angebot-anfordern" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Angebot anfordern
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Leistungen</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/leistungen#schwarmspeicher" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Kleinspeicher / Schwarmspeicher
                </Link>
              </li>
              <li>
                <Link to="/leistungen#batteriespeicher" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Batteriespeicher (BESS)
                </Link>
              </li>
              <li>
                <Link to="/leistungen#freiflaechen" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Freiflächen-PV
                </Link>
              </li>
              <li>
                <Link to="/leistungen#dachverpachtung" className="text-white/70 hover:text-[#E07000] transition-colors">
                  Dachverpachtung
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6">Kontakt</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#F28A0D] flex-shrink-0 mt-1" />
                <span className="text-white/70">
                  Kirsten und Klein eGbR Roofshare Solutions<br />
                  handelnd unter der Bezeichnung EN:POWER<br />
                  Haferfeld 3<br />
                  06485 Quedlinburg
                </span>
              </li>
              <li>
                <a href="tel:+491608732681" className="flex items-center gap-3 text-white/70 hover:text-[#E07000] transition-colors">
                  <Phone className="w-5 h-5 text-[#E07000]" />
                  +49 160 8732681
                </a>
              </li>
              <li>
                <a href="mailto:info@en-power.eu" className="flex items-center gap-3 text-white/70 hover:text-[#E07000] transition-colors">
                  <Mail className="w-5 h-5 text-[#E07000]" />
                  info@en-power.eu
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/60">
            <p>&copy; {new Date().getFullYear()} EN:POWER. Alle Rechte vorbehalten.</p>
            <div className="flex gap-6">
              <Link to="/impressum" className="hover:text-[#E07000] transition-colors">
                Impressum
              </Link>
              <Link to="/datenschutz" className="hover:text-[#E07000] transition-colors">
                Datenschutz
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
